import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';

import {
  ImageViewer,
  ViewerImage,
} from '../../../../../components/organisms/AttachmentsPanel/ImageViewer';
import {
  ShownField,
  toAnswerRows,
  useInteractionAttachments,
} from '../../../../../components/organisms/InteractionAnswers';
import { isSafeUrl } from '../../../../../utils/common';

/** Strips the native button chrome so only the thumbnail itself is visible. */
const buttonResetSx = {
  display: 'block',
  p: 0,
  border: 'none',
  background: 'none',
  cursor: 'zoom-in',
  borderRadius: 0.5,
  '&:focus-visible': { outline: '2px solid', outlineColor: 'v4.accent', outlineOffset: 2 },
};

const thumbnailSx = {
  display: 'block',
  mt: 0.25,
  maxWidth: 180,
  maxHeight: 70,
  objectFit: 'contain' as const,
  border: '1px solid',
  borderColor: 'v4.border',
  borderRadius: 0.5,
  backgroundColor: 'common.white',
};

/**
 * The answers of one interaction, as `Label: value` rows.
 *
 * Every image in the interaction — a captured signature and each uploaded photo
 * alike — becomes a thumbnail that opens the shared full-screen viewer, and they all
 * go in as one set, so the viewer's arrows and dots browse between them rather than
 * showing a single picture at a time.
 *
 * The mobile timeline shows the same answers through `MobileInteractionAnswers`; the
 * classification is shared, only the markup and the viewer differ.
 */
export const InteractionFieldRows: React.FC<{
  fields: ShownField[];
  serviceId?: number;
  compact?: boolean;
}> = ({ fields, serviceId, compact }) => {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const resolveFile = useInteractionAttachments(serviceId);

  const { rows, images } = useMemo(() => toAnswerRows(fields, resolveFile), [fields, resolveFile]);

  const fontSize = compact ? '11px' : '12px';

  return (
    <>
      {rows.map(({ field, value }, i) => (
        <Box key={i} sx={{ mt: i === 0 ? 0 : 0.25 }}>
          {field.label && (
            <Typography component="span" sx={{ fontSize, fontWeight: 600 }}>
              {field.label}:{' '}
            </Typography>
          )}
          {value.kind === 'image' ? (
            // A real button, so the thumbnail is reachable and operable by keyboard
            // as well as by pointer.
            <Box
              component="button"
              type="button"
              onClick={() => setViewerIndex(images.findIndex((img) => img.url === value.src))}
              aria-label={`View ${field.label || value.alt} full size`}
              sx={buttonResetSx}
            >
              <Box component="img" src={value.src} alt={value.alt} sx={thumbnailSx} />
            </Box>
          ) : value.kind === 'link' ? (
            <Link
              component="button"
              type="button"
              underline="hover"
              // Guarded at resolution time as well; an unexpected scheme must not open.
              onClick={() =>
                isSafeUrl(value.url) && window.open(value.url, '_blank', 'noopener,noreferrer')
              }
              sx={{ fontSize, color: 'v4.accent', verticalAlign: 'baseline' }}
            >
              {value.fileName}
            </Link>
          ) : (
            <Typography component="span" sx={{ fontSize }}>
              {value.text}
            </Typography>
          )}
        </Box>
      ))}

      {viewerIndex !== null && viewerIndex >= 0 && (
        <ImageViewer
          images={images as ViewerImage[]}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </>
  );
};
